/**
 * API client for trellis-api (mission 6 T010; FR-026, C-005, C-006; research
 * §1.9, §1.14; post-plan C4, S6).
 *
 * Every response that refuses a request carries an RFC 7807 problem with the
 * requirement ID that caused the refusal, so the UI can render the right blocked
 * band and name the remedy (style guide §7.5; `lib/problems.ts` renders it).
 *
 * URLs. `apiUrl(path)` reads `VITE_API_BASE` at call time and returns the base
 * plus the path in the base's own form: `/v1` stays relative (dev, the harness),
 * `https://api.trellis.bloomcredit.io/v1` stays absolute (production, where the
 * SPA on Pages calls another origin). `apiOrigin()` is that base's origin, the
 * one origin a server-handed URL may resolve to (C4, S7).
 *
 * Credentials. `api()` and `apiNoContent()` always send `credentials: 'include'`
 * (the session is an HttpOnly cookie). They are for API URLs only; presigned
 * object-store URLs are fetched by `lib/files.ts` with `omit` (S9).
 *
 * Path parameters. Every wrapper in `lib/api/` passes ids through `idSegment()`
 * or `ruleIdSegment()`, which validate and encode before any fetch; a refusal
 * is an `InvalidIdError`, which a screen renders as not-found (S6).
 *
 * Only this module and `lib/files.ts` call fetch (C-005, FR-026, R16).
 */

/** The typed extension members of a problem (problem.go; `lib/problems.ts` PROBLEM_MEMBERS). */
export interface ProblemMembers {
  /**
   * validation-failed only. mission 7 WP14: J1, `rule` is the rule the field broke when the handler names one
   * (problem.go FieldError; credentials-api PUT table, U27).
   */
  errors?: Array<{ field: string; rule?: string; message: string }>
  /** layout-version-unknown: the versions the API accepts. */
  accepted?: string[]
  /** run-not-ready, upload-not-startable, too-many-records: the resource's live state. */
  state?: string
  /** upload-not-startable on a consumed upload: the run it started. */
  run_id?: string
  /** insufficient-credits (B.3). */
  required?: number
  balance_before?: number
  shortfall?: number
  first_cycle?: boolean
  forgiveness_remaining?: number
  reason?: string
  /** too-many-records (P.4). */
  record_count?: number
  admission_cap?: number
  record_count_is_minimum?: boolean
  /** Mission 7: the bureau a per-bureau refusal names (TX.2, TX.6, TX.9, SEC.1). */
  bureau?: string
  /** error-acknowledgment-required (TX.4): the run's ERROR count and the sentence version. */
  error_count?: number
  ack_wording_version?: string
  /** too-many-requests (SEC.6): seconds until a Test connection slot frees. */
  retry_after_seconds?: number
  /**
   * fair-use-ceiling-reached (SAT.5; mission 8 research §1.3): `used` is the settled count (answered and
   * trimmed, never pending), `ceiling` the cycle's fair-use ceiling, `resets_at` the cycle's end (RFC 3339 UTC).
   */
  used?: number
  ceiling?: number
  resets_at?: string
  /**
   * Mission 9 billing problems (billing-api "Problem types added"; research §1.18): the remedy that restores
   * access or resolves the conflict, and when read-only began (RFC 3339 UTC; absent for no-subscription).
   */
  remedy?: string
  read_only_since?: string
  /**
   * Mission 10 retention-confirmation-required (D.2; retention-api refusal 6, spec amendment 26): the exact
   * count the new window makes due, at most 100 of those runs (RFC 3339 UTC times), and whether the list is short.
   * Mission 10 WP17 (WP07 review): `completed_at` is null for a legacy run that recorded none, `current_due_at`
   * for a run held at retention 0 while its first transmit request is pending; never a zero time.
   */
  due_count?: number
  runs?: Array<{ short_id: string; completed_at: string | null; current_due_at: string | null; new_due_at: string }>
  truncated?: boolean
}

export interface Problem extends ProblemMembers {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  /** e.g. "B.3" — credits exhausted; "TX.4" — ERROR acknowledgment required. */
  requirement?: string
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    // The title only: never a URL, a status line or a detail (S7, S9).
    super(problem.title)
    this.name = 'ApiError'
  }
}

/** A path or query parameter failed validation; nothing was fetched (S6). */
export class InvalidIdError extends Error {
  constructor(readonly param: string) {
    super(`invalid ${param}`)
    this.name = 'InvalidIdError'
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const RULE_ID = /^[A-Z0-9-]+$/

/** A run, upload, transmission or assistant message id, validated and encoded for a path segment (mission 8 C9, OR-15). */
export function idSegment(id: string, param: 'run id' | 'upload id' | 'transmission id' | 'message id'): string {
  if (!UUID.test(id)) throw new InvalidIdError(param)
  return encodeURIComponent(id)
}

/** A rule id (`K1-002`), validated; for a query value (URLSearchParams encodes it). */
export function validRuleId(id: string): string {
  if (!RULE_ID.test(id)) throw new InvalidIdError('rule id')
  return id
}

/** A rule id, validated and encoded for a path segment. */
export function ruleIdSegment(id: string): string {
  return encodeURIComponent(validRuleId(id))
}

/** Refuses a query value outside its union before any fetch. */
export function oneOf<T extends string>(value: T, allowed: readonly T[], param: string): T {
  if (!allowed.includes(value)) throw new InvalidIdError(param)
  return value
}

function apiBase(): string {
  return (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/v1'
}

/** The API URL for `path` (`/runs`), in the base's own form. */
export function apiUrl(path: string): string {
  return `${apiBase().replace(/\/$/, '')}${path}`
}

/** The API base's origin; the page origin when the base is relative. */
export function apiOrigin(): string {
  return new URL(apiBase(), window.location.href).origin
}

/** The problem a refused response carries, or one built from its status. */
export async function problemFrom(res: Response): Promise<Problem> {
  try {
    return (await res.json()) as Problem
  } catch {
    /* a non-JSON error body is still an error */
    return { type: 'about:blank', title: res.statusText, status: res.status }
  }
}

/** Hears every refused API answer before it is thrown (`lib/session.ts`). */
export type ProblemListener = (problem: Problem) => void

let problemListener: ProblemListener | undefined

/**
 * Registers the one listener that hears every refused API answer (mission 6
 * review follow-up 14, A-1): main.tsx installs the session-expiry handler here
 * so a 401 from any call, polled, mutated or direct, reaches it. Returns the
 * uninstaller; a later install replaces the earlier one.
 */
export function setProblemListener(listener: ProblemListener): () => void {
  problemListener = listener
  return () => {
    if (problemListener === listener) problemListener = undefined
  }
}

/** The ApiError for a refused response, after the listener has heard it. */
export async function refusal(res: Response): Promise<ApiError> {
  const problem = await problemFrom(res)
  problemListener?.(problem)
  return new ApiError(problem)
}

async function send(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(apiUrl(path), {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!res.ok) throw await refusal(res)
  return res
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await send(path, init)
  return (await res.json()) as T
}

/** Same transport and problem handling as `api()`, for routes that answer 204. */
export async function apiNoContent(path: string, init?: RequestInit): Promise<void> {
  await send(path, init)
}
